'use client'

import { useState, type ReactNode } from 'react'
import { GitBranch, Bot, Zap, ChevronDown, ChevronRight } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { Card, CardContent } from '@/components/ui/card'
import { isKafkaEnabled } from '@/lib/features'

interface FlowStatusIndicatorProps {
  isFlowActive: boolean
  flowSession?: {
    id: string
    flowId: number
    flowName?: string
    currentNodeId?: string
    flowData?: Record<string, any>
  } | null
  isProcessingFlow?: boolean
  flowError?: string | null
  hasDefaultFlow?: boolean | null
  defaultBot?: {
    id: number
    name: string
    slug: string
    status?: number
  } | null
  className?: string
  isCollapsible?: boolean
  defaultCollapsed?: boolean
}

export function FlowStatusIndicator({
  isFlowActive,
  flowSession,
  isProcessingFlow = false,
  flowError = null,
  hasDefaultFlow = null,
  defaultBot = null,
  className = '',
  isCollapsible = true,
  defaultCollapsed = true,
}: FlowStatusIndicatorProps) {
  const [isCollapsed, setIsCollapsed] = useState(
    isCollapsible ? defaultCollapsed : false
  )

  // Show flow errors in development, hide in production
  const isDevelopment = process.env.NODE_ENV === 'development'

  if (!isKafkaEnabled) {
    return <div className="hidden"></div>
    // do not remove below
    return (
      <div
        className={`border-l-yellow-500 bg-yellow-50 text-yellow-800 ${className}`}
      >
        <div className="flex items-center gap-2">
          <Zap className="h-4 w-4" />
          <span className="font-medium">
            Flow status temporarily unavailable
          </span>
          <Badge
            variant="outline"
            className="border-yellow-300 text-[10px] text-yellow-800"
          >
            Disabled
          </Badge>
        </div>
        <p className="text-sm">
          Real-time flow monitoring depends on Kafka, which is currently turned
          off.
        </p>
      </div>
    )
  }

  // Debug logging removed to reduce console noise

  // Determine what to show:
  // 1. If flow is active AND has valid session - show active status
  // 2. If flow is not active but flows are available (hasDefaultFlow = true) - show "Flow Available"
  // 3. If no flows available and bot is available - show "AI Assistant Available"
  // 4. If no flows available and no bot - show "No Response Available"
  // 5. If there's an error and we're in development AND no other options - show error
  const shouldShowActive = !!(
    isFlowActive &&
    !flowError &&
    flowSession &&
    flowSession.flowId
  )
  const shouldShowFlowAvailable = isFlowActive && !flowError

  const isBotInactive = !!(defaultBot && defaultBot.status !== 1)
  const shouldShowBot = !!(
    !isFlowActive &&
    hasDefaultFlow === false &&
    defaultBot &&
    !isBotInactive
  )
  const shouldShowNoResponse = !!(
    !isFlowActive &&
    hasDefaultFlow === false &&
    (!defaultBot || isBotInactive)
  )
  const shouldShowError = !!(
    flowError &&
    isDevelopment &&
    !shouldShowActive &&
    !shouldShowFlowAvailable &&
    !shouldShowBot &&
    !shouldShowNoResponse
  )

  // Debug logging removed to reduce console noise

  if (
    !shouldShowError &&
    !shouldShowActive &&
    !shouldShowFlowAvailable &&
    !shouldShowBot &&
    !shouldShowNoResponse
  ) {
    return null
  }
  // console.log('flowSession?', flowSession)
  let headerIcon = null
  let headerText = ''
  let headerTextClass = 'text-blue-800'
  let badge: ReactNode = null
  let body: ReactNode = null
  let trailingIndicator: ReactNode = null
  let cardToneClass = 'border-l-blue-500 bg-blue-50'

  if (shouldShowError) {
    headerIcon = <Zap className="h-4 w-4 text-red-600" />
    headerText = 'Flow Error'
    headerTextClass = 'text-red-800'
    badge = (
      <Badge variant="destructive" className="text-[10px]">
        Error
      </Badge>
    )
    body = <p className="text-red-700">{flowError}</p>
    cardToneClass = 'border-l-red-500 bg-red-50'
  } else if (shouldShowActive) {
    const label = isProcessingFlow
      ? 'Processing Flow...'
      : flowSession
        ? `${flowSession.flowName ? `${flowSession.flowName} ` : ''}Flow Active`
        : 'Flow Active'

    headerIcon = (
      <GitBranch
        className={`h-4 w-4 ${
          isProcessingFlow ? 'text-blue-600 animate-pulse' : 'text-blue-600'
        }`}
      />
    )
    headerText = label
    headerTextClass = 'text-blue-800'
    badge = (
      <Badge
        variant="outline"
        className="text-[10px] border-blue-300 text-blue-700"
      >
        {isProcessingFlow ? 'Processing' : 'Active'}
      </Badge>
    )
    trailingIndicator = isProcessingFlow ? (
      <div className="w-2 h-2 bg-blue-600 rounded-full animate-pulse" />
    ) : null
    body = (
      <div className="space-y-1 text-blue-600">
        {flowSession?.currentNodeId && (
          <div>Node: {flowSession.currentNodeId.slice(-8)}</div>
        )}
        <div>Your conversation is being guided by a structured flow.</div>
      </div>
    )
    cardToneClass = 'border-l-blue-500 bg-blue-50'
  } else if (shouldShowFlowAvailable) {
    headerIcon = <GitBranch className="h-4 w-4 text-green-600" />
    headerText = 'Flow Available'
    headerTextClass = 'text-green-800'
    badge = (
      <Badge
        variant="outline"
        className="text-[10px] border-green-300 text-green-700"
      >
        Ready
      </Badge>
    )
    body = (
      <p className="text-green-700">
        Send a message to start the conversation flow
      </p>
    )
    cardToneClass = 'border-l-green-500 bg-green-50'
  } else if (shouldShowBot) {
    headerIcon = <Bot className="h-4 w-4 text-green-600" />
    headerText = `${defaultBot?.name || 'AI Assistant'} Available`
    headerTextClass = 'text-green-800'
    badge = (
      <Badge
        variant="outline"
        className="text-[10px] border-green-300 text-green-700"
      >
        Ready
      </Badge>
    )
    body = (
      <p className="text-green-600">
        Chat with {defaultBot?.name || 'our AI assistant'} for general
        inquiries.
        {isDevelopment && ' No flows configured for this site.'}
      </p>
    )
    cardToneClass = 'border-l-green-500 bg-green-50'
  } else {
    const botInactiveMessage = isBotInactive
      ? `${defaultBot?.name || 'AI Assistant'} is currently inactive`
      : 'No Response Available'

    headerIcon = <Bot className="h-4 w-4 text-red-600" />
    headerText = botInactiveMessage
    headerTextClass = 'text-red-800'
    badge = (
      <Badge
        variant="outline"
        className="text-[10px] border-red-300 text-red-700"
      >
        {isBotInactive ? 'Inactive' : 'Offline'}
      </Badge>
    )
    body = (
      <p className="text-red-600">
        {isBotInactive
          ? `The AI assistant "${defaultBot?.name}" is currently inactive and cannot respond to messages.`
          : 'No AI assistant or flows are currently available.'}
        {isDevelopment && ' Configure a bot or flow for this site.'}
      </p>
    )
    cardToneClass = 'border-l-red-500 bg-red-50'
  }

  return (
    <div className={`border-l-4 p-2 text-xs ${cardToneClass} ${className}`}>
      {isCollapsible ? (
        <button
          type="button"
          className="w-full flex items-center gap-2 text-left"
          onClick={() => setIsCollapsed(prev => !prev)}
        >
          <div className="flex items-center gap-2 flex-1">
            {headerIcon}
            <span className={`font-medium ${headerTextClass}`}>
              {headerText}
            </span>
            {badge}
          </div>
          <div className="flex items-center gap-2 text-gray-500">
            {trailingIndicator}
            {isCollapsed ? (
              <ChevronRight className="h-3 w-3" />
            ) : (
              <ChevronDown className="h-3 w-3" />
            )}
          </div>
        </button>
      ) : (
        <div className="flex items-center gap-2">
          {headerIcon}
          <span className={`font-medium ${headerTextClass}`}>{headerText}</span>
          {badge}
          {trailingIndicator}
        </div>
      )}

      {!isCollapsed && body && <div className="mt-0 space-y-1">{body}</div>}
    </div>
  )
}

export default FlowStatusIndicator
